<template>
    <div
        v-if="!loaded"
        class="flex items-center justify-center w-full py-12"
    >
        <Loader />
    </div>

    <template v-else>
        <!-- No card chrome — the form reads as a natural page section like
             Reviews, House Information, etc. The "Get your exact price" heading lives in
             single-property.php (via the components/property-content view) so it
             matches every other section heading on the page; this template starts
             at the success/error messages and the form itself.
             `@container` stays so the form's responsive column rules respond to
             the form's own width; max-w-5xl + mx-auto keeps inputs from spanning
             too wide on very large viewports. -->
        <div class="@container max-w-5xl mx-auto">
            <div
                v-if="errorMessage"
                ref="error"
                class="pb-small"
            >
                <FormError :message="errorMessage" />
            </div>

            <form
                v-if="!successMessage"
                ref="form"
                novalidate
                :data-confirm-collapsed="showConfirm ? null : 'true'"
                @focusin="markFormStarted"
                @submit.prevent="submitForm"
            >
                <!-- Honeypot: hidden from real users (off-screen, not tabbable,
                     aria-hidden, autocomplete off). Bots that fill every field trip
                     it and the server holds the submission. Never remove the label
                     association or a screen reader may still announce it. -->
                <div
                    aria-hidden="true"
                    class="absolute -left-[9999px] top-0 h-0 w-0 overflow-hidden"
                >
                    <label for="enquiry-website">Website</label>
                    <input
                        id="enquiry-website"
                        v-model="formData.website"
                        type="text"
                        name="website"
                        tabindex="-1"
                        autocomplete="off"
                    >
                </div>

                <!-- About your stay | Your details: stacked single-col by default,
                     two-up only when the form card itself is ≥ 56rem (@4xl). Below
                     that — including the constrained main-column case on lg+ where
                     the card is ~580px wide — sections stack so labels never wrap.
                     The outer grid-gap (not the form-row sibling divider, which is
                     suppressed in #enquiry-form scoped CSS) separates the sections -
                     it is the ONLY thing that does, since the headings came out, so
                     it is trimmed rather than removed where the stacked layout made
                     it read as a stray gap under the last field. -->
                <div class="grid grid-cols-1 @4xl:grid-cols-2 gap-8 @4xl:gap-12">
                    <!-- 1. Desired dates first — the ONE editable date control, grouped
                         with the availability checkbox at the top of the form (spans both
                         columns of the existing grid; the grid itself is unchanged). The
                         Availability calendar is a picker that writes into this field. -->
                    <fieldset class="form-row @4xl:col-span-2">
                        <div
                            id="enquiry-dates"
                            class="form-col scroll-mt-28"
                        >
                            <div class="flex items-baseline justify-between gap-3">
                                <label class="label">
                                    Desired dates (optional)
                                </label>

                                <a
                                    v-if="availability.length"
                                    href="#availability"
                                    class="text-link text-xssss shrink-0"
                                    @click.prevent="scrollToCalendar"
                                >
                                    Availability calendar
                                </a>
                            </div>

                            <DatesSelector
                                :showOptional="false"
                                :startDate="enquiryDates.from"
                                :endDate="enquiryDates.to"
                                :availableDates="availability"
                                @datesSelected="onDatesSelected"
                            />

                            <!-- Night count + automatic availability state (both breakpoints).
                                 The range is checked against the property's availability the
                                 moment both dates are set; a booked range additionally opens
                                 the opt-in notice below before Send is enabled. -->
                            <p
                                v-if="nights"
                                class="mt-1.5 text-xsss font-semibold text-brand-2-950 inline-flex items-center gap-1.5"
                            >
                                <span>{{ nights }} night{{ nights === 1 ? '' : 's' }}</span>

                                <!-- The price for these dates belongs in this line, not in a
                                     separate block above that repeats the dates and night
                                     count. Renders nothing when the stay isn't quotable. -->
                                <enquirypricesummary
                                    variant="inline"
                                    :property-id="propertyId"
                                ></enquirypricesummary>

                                <template v-if="availabilityState === 'available'">
                                    <span aria-hidden="true" class="text-brand-2-950/40">·</span>
                                    <span class="size-1.5 rounded-full bg-emerald-500"></span>
                                    <span class="text-emerald-700">Available</span>
                                </template>
                                <template v-else-if="availabilityState === 'unavailable'">
                                    <span aria-hidden="true" class="text-brand-2-950/40">·</span>
                                    <span class="size-1.5 rounded-full bg-amber-500"></span>
                                    <span class="text-amber-700">Not available</span>
                                </template>
                            </p>

                            <!-- Why there is no figure in the line above, for the stay
                                 lengths the theme does not price. Renders nothing otherwise;
                                 the availability state above is untouched. -->
                            <enquirypricesummary
                                v-if="nights"
                                variant="note"
                                :property-id="propertyId"
                            ></enquirypricesummary>

                            <!-- Booked-dates gate: dates the calendar shows as booked need an
                                 explicit opt-in before Send enables. The tick ships as the real
                                 availability_checked value, which the server enforces. Alternative
                                 houses confirmed free on the same dates are offered as plain links
                                 to their listings — view-first, no shortcut sends. -->
                            <div
                                v-if="availabilityState === 'unavailable'"
                                class="mt-4 border border-amber-500/40 bg-amber-50 rounded-xl p-5 space-y-4"
                            >
                                <p class="text-xsss">
                                    These dates show as booked on the availability calendar.
                                    Choose different dates, or send your enquiry anyway, but
                                    note {{ propertyName }} is <strong>unlikely to be available</strong>
                                    on {{ formatShortRange(enquiryDates.from, enquiryDates.to) }}.
                                </p>

                                <!-- Tier 1 (owner-favourable): nearest same-shape dates free at
                                     THIS house. Tapping one sets the enquiry to those dates, which
                                     flips the range to Available and hides this whole notice. -->
                                <div
                                    v-if="sameDateAlternatives.length"
                                    class="space-y-2"
                                >
                                    <p class="text-xsss font-semibold">
                                        Available dates at {{ propertyName }}
                                    </p>

                                    <div class="flex flex-wrap gap-2">
                                        <button
                                            v-for="alt in sameDateAlternatives"
                                            :key="alt.startDate"
                                            type="button"
                                            class="inline-flex items-center rounded-full border border-brand-2-950/20 bg-white px-3 py-1.5 text-xsss hover:border-brand-2-950/50 lh-transition"
                                            @click="applyAlternativeDates(alt)"
                                        >
                                            {{ formatShortRange(alt.startDate, alt.endDate) }} &middot; {{ alt.nights }} night{{ alt.nights === 1 ? '' : 's' }}
                                        </button>
                                    </div>
                                </div>

                                <div
                                    v-if="alternatives.length"
                                    class="pt-4 border-t border-amber-500/30 space-y-3"
                                >
                                    <p class="text-xsss font-semibold">
                                        Similar houses to consider
                                    </p>

                                    <ul class="space-y-2.5">
                                        <li
                                            v-for="house in alternatives"
                                            :key="house.id"
                                        >
                                            <a
                                                :href="house.url"
                                                class="flex items-center gap-3 group"
                                            >
                                                <img
                                                    v-if="house.image"
                                                    :src="house.image"
                                                    :alt="house.name"
                                                    class="size-12 rounded-lg object-cover shrink-0"
                                                    loading="lazy"
                                                >

                                                <span class="min-w-0">
                                                    <span class="block text-xsss font-semibold underline decoration-transparent group-hover:decoration-inherit lh-transition">
                                                        {{ house.name }}
                                                    </span>

                                                    <span class="block text-xssss text-brand-2-950/70">
                                                        <template v-if="house.region">{{ house.region }}</template>
                                                        <template v-if="house.region && house.sleeps"> &middot; </template>
                                                        <template v-if="house.sleeps">Sleeps {{ house.sleeps }}</template>
                                                    </span>

                                                    <span
                                                        class="block text-xssss font-semibold"
                                                        :class="house.confirmed ? 'text-emerald-700' : 'text-brand-2-950/60'"
                                                    >
                                                        {{ house.confirmed ? 'Available on your dates' : 'Availability not shown – worth enquiring' }}
                                                    </span>
                                                </span>
                                            </a>
                                        </li>
                                    </ul>
                                </div>

                                <!-- Send-anyway last: the escape hatch after every better
                                     option (shift dates here, or a similar house), so the
                                     low-quality booked-dates enquiry is the final fallback.
                                     A divider (matching the one above the houses) sets it
                                     apart as its own step. -->
                                <div class="pt-4 border-t border-amber-500/30">
                                    <!-- The whole row is the label, not just the text beside the
                                         box: a 26px box plus a 12px dead gap is a thin target for
                                         a thumb, and a near-miss lands on the notice and does
                                         nothing — which reads as a checkbox that doesn't work.
                                         Wrapping in <label for> makes the box, the gap and the
                                         full-width text one hit area. -->
                                    <label
                                        for="send-anyway"
                                        class="inline-label py-1 -my-1"
                                    >
                                        <input
                                            id="send-anyway"
                                            class="checkbox"
                                            type="checkbox"
                                            v-model="sendAnyway"
                                        >

                                        <span class="small-label">
                                            Send anyway. I realise these dates are unavailable.
                                        </span>
                                    </label>
                                </div>
                            </div>

                            <InlineValidation
                                v-model:errors="validationErrors"
                                errorKey="dates"
                            />
                        </div>
                    </fieldset>

                    <!-- 2. About your stay -->
                    <!-- No section heading. "About your stay" and "Your details"
                         announced two chunks to get through on a nine-field form
                         whose grouping the fields already make obvious, and cost
                         ~120px of the height that made it read as long. The outer
                         grid gap is what separates the two groups now. -->
                    <fieldset class="form-row">
                        <div class="form-col">
                            <label
                                for="group-type"
                                class="label"
                            >
                                Group type
                                <span class="form-required">*</span>
                            </label>

                            <select
                                id="group-type"
                                class="select"
                                required
                                v-model="formData.group_type"
                                @change="persistField('group_type')"
                            >
                                <option
                                    value=""
                                    disabled
                                    selected
                                >
                                    Please select...
                                </option>
                                <option>
                                    Family holidays
                                </option>
                                <option>
                                    Groups of friends
                                </option>
                                <option>
                                    Hen parties
                                </option>
                                <option>
                                    Stag parties
                                </option>
                                <option>
                                    Weddings
                                </option>
                                <option>
                                    Business groups
                                </option>
                            </select>

                            <InlineValidation
                                v-model:errors="validationErrors"
                                errorKey="group_type"
                            />
                        </div>

                        <!-- Adults | Children: paired from @xs (a 20rem card) rather than
                             @sm, so they share a row on a phone instead of taking one each -
                             the labels are short enough to hold a half-width column without
                             wrapping (checked at 390px and 360px; below that the card drops
                             under 20rem and they stack again). Container-query-based so the
                             pairing respects the form card's own width, not the viewport. -->
                        <div class="col-span-full flex flex-col @xs:flex-row gap-4 @xs:gap-[1.2rem] xl:gap-[1.8rem]">
                            <div class="form-col flex-1">
                                <label
                                    for="adults-number"
                                    class="label"
                                >
                                    Adults
                                </label>

                                <input
                                    id="adults-number"
                                    class="input"
                                    type="number"
                                    name="adults_number"
                                    v-model="formData.adults_number"
                                    @blur="persistField('adults_number')"
                                >

                                <InlineValidation
                                    v-model:errors="validationErrors"
                                    errorKey="adults_number"
                                />
                            </div>

                            <div class="form-col flex-1">
                                <label
                                    for="children-number"
                                    class="label"
                                >
                                    Children
                                </label>

                                <input
                                    id="children-number"
                                    class="input"
                                    type="number"
                                    name="children_number"
                                    v-model="formData.children_number"
                                    @blur="persistField('children_number')"
                                >

                                <InlineValidation
                                    v-model:errors="validationErrors"
                                    errorKey="children_number"
                                />
                            </div>
                        </div>

                        <!-- Event details opens on request. A textarea is the most
                             intimidating control on the form - it reads as homework -
                             and this one is optional, which its own placeholder says.
                             Behind a link it costs a line instead of ~90px, and the
                             link names what it opens so nothing is hidden. It starts
                             open whenever there is already a value (a localStorage
                             restore, a PHP prefill), so returning text is never
                             concealed behind a link that looks like it adds nothing. -->
                        <div
                            v-if="!eventDetailsOpen"
                            class="form-col enquiry-add-details-row"
                        >
                            <!-- A ghost pill rather than a text link: set in the body
                                 ink with a full-width underline it read as a section
                                 heading with a rule under it, not as something to
                                 press - and a 21px line is under half a thumb. -->
                            <button
                                type="button"
                                class="button button-ghost button-small enquiry-add-details self-start"
                                @click="openEventDetails"
                            >
                                <!-- size, not w: plus.svg carries width="12"
                                     height="12", and a CSS width alone overrides
                                     only the width attribute - the icon rendered
                                     10 x 12, stretched, and overshot an 8px cap
                                     height by 2px above and below. -->
                                <span
                                    class="[&_svg]:size-[10px] [&_svg]:fill-current inline-flex"
                                    v-html="PlusIcon"
                                ></span>

                                Add event details (optional, recommended)
                            </button>
                        </div>

                        <div
                            v-else
                            class="form-col"
                        >
                            <label
                                for="event-details"
                                class="label"
                            >
                                Event details
                            </label>

                            <textarea
                                id="event-details"
                                ref="eventDetails"
                                class="textarea"
                                rows="3"
                                placeholder="e.g. 50th birthday, anniversary"
                                v-model="formData.event_details"
                                @blur="persistField('event_details')"
                            ></textarea>

                            <InlineValidation
                                v-model:errors="validationErrors"
                                errorKey="event_details"
                            />
                        </div>
                    </fieldset>

                    <fieldset class="form-row">
                        <!-- First | Last: the same @xs flex pairing as Adults | Children,
                             so the two shortest labels on the form share a row on a phone
                             instead of taking one each. Email, Confirm and Phone stay full
                             width on purpose - "Confirm email address" wraps to two lines at
                             half width, and a half-width email input hides the end of a long
                             address while it is being typed. -->
                        <div class="col-span-full flex flex-col @xs:flex-row gap-4 @xs:gap-[1.2rem] xl:gap-[1.8rem]">
                            <div class="form-col flex-1">
                                <label
                                    for="first-name"
                                    class="label"
                                >
                                    First name
                                    <span class="form-required">*</span>
                                </label>

                                <input
                                    id="first-name"
                                    class="input"
                                    type="text"
                                    required
                                    v-model="formData.first_name"
                                    @blur="validateField('first_name'); persistField('first_name')"
                                >

                                <InlineValidation
                                    v-model:errors="validationErrors"
                                    errorKey="first_name"
                                />
                            </div>

                            <div class="form-col flex-1">
                                <label
                                    for="last-name"
                                    class="label"
                                >
                                    Last name
                                    <span class="form-required">*</span>
                                </label>

                                <input
                                    id="last-name"
                                    class="input"
                                    type="text"
                                    required
                                    v-model="formData.last_name"
                                    @blur="validateField('last_name'); persistField('last_name')"
                                >

                                <InlineValidation
                                    v-model:errors="validationErrors"
                                    errorKey="last_name"
                                />
                            </div>
                        </div>

                        <div class="form-col form-col-2">
                            <label
                                for="email"
                                class="label"
                            >
                                Email address
                                <span class="form-required">*</span>
                            </label>

                            <input
                                id="email"
                                class="input"
                                type="email"
                                required
                                v-model="formData.email"
                                @blur="validateField('email'); persistField('email')"
                            >

                            <InlineValidation
                                v-model:errors="validationErrors"
                                errorKey="email"
                            />
                        </div>

                        <!-- Directly after the address it confirms. It used to sit below
                             Phone because a collapsed grid item still held its cell and
                             left an empty half-row beside Email; the collapsed state now
                             takes it out of grid flow entirely (position: absolute, see
                             enquiry-form.css), so Phone moves up into that slot on its
                             own and the pairing reads correctly when Confirm opens. -->
                        <div class="form-col form-col-2 enquiry-confirm">
                            <label
                                for="email-confirm"
                                class="label"
                            >
                                Confirm email address
                                <span class="form-required">*</span>
                            </label>

                            <input
                                id="email-confirm"
                                class="input"
                                type="email"
                                :required="showConfirm"
                                v-model="formData.email_confirm"
                                @blur="validateField('email_confirm')"
                            >

                            <InlineValidation
                                v-model:errors="validationErrors"
                                errorKey="email_confirm"
                            />
                        </div>

                        <div class="form-col form-col-2">
                            <label
                                for="phone"
                                class="label"
                            >
                                Phone (optional)
                            </label>

                            <input
                                id="phone"
                                class="input"
                                type="tel"
                                v-model="formData.phone"
                                @blur="persistField('phone')"
                            >
                        </div>

                        <div class="form-col">
                            <div class="inline-label">
                                <input
                                    id="newsletter"
                                    class="checkbox"
                                    type="checkbox"
                                    value="1"
                                    v-model="formData.newsletter_subscribe"
                                    @change="persistField('newsletter_subscribe')"
                                >
                                <label
                                    for="newsletter"
                                    class="small-label"
                                >
                                    Please send me occasional special offers by email
                                </label>
                            </div>

                            <InlineValidation
                                v-model:errors="validationErrors"
                                errorKey="newsletter_subscribe"
                            />
                        </div>
                    </fieldset>
                </div>

                <!-- Submit: full-width on mobile/tablet for tap target, auto-width
                     (intrinsic + lg:px-12 padding) on desktop so it reads as a
                     proper CTA rather than a stretched bar.

                     Desktop sits closer than the gap BETWEEN fieldsets
                     (gap-12 there): across the wider column that distance read
                     as the button floating under the form rather than being the
                     end of it. Mobile keeps its roomier mt-10, where the form is
                     narrow and the button is the next thing under a thumb.

                     Label: "Get my price" (was "Send Now" until Oct 2026). The form
                     is how a guest gets an exact figure, and the button should say
                     so. While sending it reads "Sending…" – the spinner alone did
                     not say what the button was doing, and "Getting price…" would
                     promise an instant one. Type, classes, :disabled and the
                     spinner are untouched: GTM matches this button on the form's
                     id and the dataLayer events, never on its text. -->
                <div class="mt-10 lg:mt-8">
                    <button
                        class="button button-2 w-full lg:w-auto lg:px-12"
                        :disabled="submitting || (availabilityState === 'unavailable' && !sendAnyway)"
                    >
                        {{ submitting ? 'Sending…' : 'Get my price' }}

                        <span
                            v-if="submitting"
                            class="size-4 text-white"
                            v-html="SpinnerIcon"
                        ></span>
                    </button>

                    <!-- Tell the user WHY Send is disabled — the booked-dates gate
                         disables it until "Send anyway" is ticked (or the dates
                         change), otherwise the greyed button looks broken. -->
                    <p
                        v-if="availabilityState === 'unavailable' && !sendAnyway"
                        class="mt-3 text-xsss text-amber-700"
                    >
                        Choose available dates above, or tick &ldquo;Send anyway&rdquo; to enquire about these booked dates.
                    </p>

                    <!-- Quiet fine print closing the form — no panel/box. Credit-cards
                         line first (where the property accepts them), then the payment
                         terms beneath. Renders nothing when there's neither. -->
                    <div
                        v-if="cardPaymentsIcon || paymentTerms"
                        class="mt-8 space-y-1.5 text-xssss md:text-xsss text-brand-2-950/60"
                    >
                        <p
                            v-if="cardPaymentsIcon"
                            class="flex items-center gap-1.5"
                        >
                            <span
                                class="size-4 shrink-0 [&>svg]:size-full"
                                v-html="cardPaymentsIcon"
                            ></span>
                            Credit cards accepted
                        </p>

                        <p
                            v-if="paymentTerms"
                            class="max-w-prose"
                        >
                            <span class="text-brand-2-950/80">Payment terms:</span>
                            {{ paymentTerms }}
                        </p>
                    </div>
                </div>
            </form>

            <!-- Confirmation + similar-houses, wrapped as ONE section so the
                 suggestions read as a continuation of the confirmation rather than a
                 detached block. Toned-down green (subtle emerald tint, not a solid
                 fill). Form is v-if'd out above on success so this renders in its
                 place; scrollToSuccess() targets this wrapper. -->
            <div
                v-if="successMessage"
                ref="success"
                class="scroll-mt-[calc(var(--header-height)+var(--wp-toolbar-height)+16px)] rounded-xl border border-emerald-200/70 overflow-hidden"
            >
                <div class="bg-emerald-50/70 p-6 sm:p-8 text-center space-y-2">
                    <span class="size-[42px] inline-block bg-has-success"></span>
                    <!-- "Sent", not "Success": the line under it (from
                         EnquiryController::successMessage) says a quote is on its
                         way, and the heading names the thing that happened. -->
                    <h2 class="heading h6">
                        Sent
                    </h2>
                    <ul>
                        <li v-html="successMessage"></li>
                    </ul>

                    <!-- The safety net, on its own line and at fine-print size: it
                         backs up the confirmation rather than competing with it,
                         and reads as a footnote instead of part of the news. -->
                    <p
                        v-if="successFollowUp"
                        class="text-xssss md:text-xsss text-brand-2-950/70"
                        v-text="successFollowUp"
                    ></p>

                </div>

                <!-- Similar houses — same card, divider above. A loader shows while
                     the /similar fetch runs (it isn't instant), then the cards; the
                     whole sub-section is hidden if nothing usable came back (no empty
                     state). Saved houses re-send the identical enquiry with one tap;
                     the rest link to the listing (see the CTA comment below). -->
                <div
                    v-if="suggestionsLoading || suggestions.length"
                    class="border-t border-emerald-200/70 bg-white p-6 sm:p-8 space-y-6"
                >
                    <div class="space-y-1">
                        <p class="form-heading">
                            Enquire with similar houses
                        </p>
                        <p class="text-xsss text-brand-2-950/70">
                            Available on your dates.
                        </p>
                    </div>

                    <div
                        v-if="suggestionsLoading"
                        class="flex items-center gap-2.5 text-xsss text-brand-2-950/70"
                    >
                        <span
                            class="size-4 text-brand-2-950/50"
                            v-html="SpinnerIcon"
                        ></span>
                        Finding houses available on your dates…
                    </div>

                    <template v-else>
                        <ul class="grid grid-cols-1 @2xl:grid-cols-3 gap-6">
                    <li
                        v-for="house in suggestions"
                        :key="house.id"
                        class="flex flex-col border border-brand-2-950/10 rounded-xl overflow-hidden shadow-4xl"
                    >
                        <a
                            :href="house.url"
                            class="relative block aspect-433/242 bg-beige"
                        >
                            <img
                                v-if="house.image"
                                :src="house.image"
                                :alt="house.name"
                                class="w-full h-full object-cover"
                                loading="lazy"
                            />

                            <span
                                class="absolute top-3 left-3 inline-flex items-center rounded-full bg-white/95 px-3 py-1 text-xssss font-semibold"
                                :class="house.confirmed ? 'text-green-700' : 'text-brand-2-950/70'"
                            >
                                {{ house.badge }}
                            </span>

                            <!-- Provenance chip: this house is one the visitor saved.
                                 Sits opposite the availability badge so the two never
                                 collide. -->
                            <span
                                v-if="house.favourite"
                                class="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-xssss font-semibold text-brand-2-950/70"
                            >
                                <span class="size-3 text-red-500 [&>svg]:size-full" v-html="heartFilledIcon"></span>
                                Saved
                            </span>
                        </a>

                        <div class="flex flex-col flex-1 gap-3 p-5">
                            <div class="space-y-1">
                                <p
                                    v-if="house.region"
                                    class="text-xssss uppercase tracking-wide text-brand-2-950/60"
                                >
                                    {{ house.region }}
                                </p>

                                <a
                                    :href="house.url"
                                    class="heading h6 block"
                                >
                                    {{ house.name }}
                                </a>
                            </div>

                            <ul class="flex flex-wrap gap-x-4 gap-y-1 text-xsss text-brand-2-950/70">
                                <li v-if="house.sleeps">
                                    Sleeps {{ house.sleeps }}
                                </li>
                                <li v-if="house.price_band">
                                    {{ house.price_band }}
                                </li>
                            </ul>

                            <!-- Only saved houses keep the one-tap send — the visitor
                                 has already reviewed those. Other suggestions link to
                                 the listing instead, so an enquiry only happens after
                                 the house has actually been looked at (the form there
                                 prefills from cookies, so re-enquiring is quick). -->
                            <button
                                v-if="house.favourite"
                                type="button"
                                class="button button-small w-full mt-auto"
                                :class="sendErrors[house.id] ? 'button-ghost !text-error !border-error/40' : 'button-2'"
                                :disabled="sentIds.includes(house.id) || sendingIds.includes(house.id)"
                                @click="sendSameEnquiry(house)"
                            >
                                <template v-if="sentIds.includes(house.id)">
                                    Sent ✓
                                </template>
                                <template v-else-if="sendErrors[house.id]">
                                    {{ sendErrors[house.id] }}
                                </template>
                                <template v-else>
                                    Send the same enquiry
                                </template>

                                <span
                                    v-if="sendingIds.includes(house.id)"
                                    class="size-4 text-white"
                                    v-html="SpinnerIcon"
                                ></span>
                            </button>

                            <a
                                v-else
                                :href="house.url"
                                class="button button-small button-2 w-full mt-auto"
                                @click="pushSimilarViewEvent(house)"
                            >
                                View house
                            </a>
                        </div>
                    </li>
                </ul>
                    </template>
                </div>
            </div>
        </div>
    </template>
</template>

<script setup>
    import axios from 'axios';

    import { ref, computed, watch, onMounted, nextTick, useTemplateRef } from 'vue';

    import {
        enquiryDates,
        setEnquiryDates,
        nightsBetween,
        rangeAvailability,
        sameShapeAvailableWindows,
        formatShortRange,
    } from '@js/src/stores/enquiryDates';

    import DatesSelector from '@js/src/components/DatesSelector.vue';
    import InlineValidation from '@js/src/components/InlineValidation.vue'
    import FormError from '@js/src/components/FormError.vue'
    import Loader from '@js/src/components/Loader.vue';

    import { getFavourites } from '@js/src/favourites';
    import { pushToDataLayer } from '@js/src/dataLayer';

    import SpinnerIcon from '@img/icons/spinner.svg?raw';
    import PlusIcon from '@img/icons/plus.svg?raw';
    import heartFilledIcon from '@img/icons/heart-filled.svg?raw';

    const successMessageElem = useTemplateRef('success');
    const errorMessageElem = useTemplateRef('error');
    const form = useTemplateRef('form');
    const eventDetailsElem = useTemplateRef('eventDetails');

    const loaded = ref(false);
    const successMessage = ref('');
    // Shipped separately from the confirmation (EnquiryController::followUpMessage)
    // so it can render on its own line as fine print, rather than as a third
    // sentence competing with the news that the message went.
    const successFollowUp = ref('');
    const errorMessage = ref('');
    const submitting = ref(false);
    const emailPasted = ref(false);

    // Event details starts closed and opens on the link. onMounted re-opens it
    // when a value survived (PHP prefill, or the localStorage seed below), so
    // text the guest already wrote is never left behind a link.
    const eventDetailsOpen = ref(false);

    // GA "form_start" fires once on first interaction inside the form, dispatched both
    // as a CustomEvent and as a dataLayer push so GTM can pick it up either way.
    const formStarted = ref(false);

    const validationErrors = ref({});

    // ─── Multi-enquiry suggestions ───────────────────────────────────────────
    // After a successful enquiry we offer up to 3 similar houses that are free on
    // the same dates so the guest can fire the identical enquiry at them with one
    // tap (or all at once). Purely additive to the success state — the single
    // enquiry flow is untouched, and the block renders nothing when empty.
    const suggestions = ref([]); // [{ id, name, url, image, sleeps, region, price_band, badge, confirmed }]
    const sentIds = ref([]);     // ids that returned success (button → "Sent ✓")
    const sendingIds = ref([]);  // ids with an in-flight send (per-card spinner)
    const sendErrors = ref({});  // { [id]: message } — failed sends, shown on the card, retryable
    const suggestionsLoading = ref(false); // /similar fetch in flight (shows a loader)

    const availability = ref(EnquiryForm.data.availability || []);

    // House name for the booked-dates notice copy ("…note <name> is unlikely to
    // be available"). Falls back generically if the global ever ships without it.
    const propertyName = ref(EnquiryForm.data.name || 'this house');
    // Bound in the template (the inline price summary), and Vue templates can't
    // read window globals like Page — only setup bindings.
    const propertyId = Page.postId;

    const formData = ref({
        from: enquiryDates.from,
        to: enquiryDates.to,
        first_name: EnquiryForm.data.first_name || '',
        last_name: EnquiryForm.data.last_name || '',
        email: EnquiryForm.data.email || '',
        email_confirm: EnquiryForm.data.email || '',
        phone: EnquiryForm.data.phone || '',
        group_type: EnquiryForm.data.group_type || '',
        adults_number: EnquiryForm.data.adults_number || '',
        children_number: EnquiryForm.data.children_number || '',
        event_details: EnquiryForm.data.event_details || '',
        newsletter_subscribe: EnquiryForm.data.newsletter_subscribe || false,
        // Verified automatically against the calendar and shown on the nights
        // line. Ships true for available/unknown ranges; for a range the calendar
        // shows as BOOKED it ships the "send anyway" opt-in tick instead (set in
        // submitForm), which the server enforces when the property publishes
        // availability.
        availability_checked: true,
        // Honeypot — must stay empty. Hidden from real users (off-screen +
        // aria-hidden + not tabbable), so a value here means a bot filled it and
        // the server holds the submission. Rides along in similar-house sends too
        // via the {...formData.value} spread.
        website: '',
    });

    // Confirm-email is only needed once Email has content and wasn't pasted (a paste
    // fills both fields silently, so the values already match). It collapses/expands
    // via the same transition the paste flow uses.
    const showConfirm = computed(() => Boolean(formData.value.email) && !emailPasted.value);

    // ─── Dates ────────────────────────────────────────────────────────────────
    // The inline "Desired dates" field is the primary control; the Availability
    // calendar is a second way in. Both write the shared enquiryDates store, and
    // this component reads it for the payload, the nights line and soft validation.
    // from/to field names + semantics are unchanged.
    function onDatesSelected(payload) {
        setEnquiryDates(payload.startDate, payload.endDate);
    }

    // Night count for the line under the dates fields (both breakpoints).
    const nights = computed(() => nightsBetween(enquiryDates.from, enquiryDates.to));

    // Automatic availability check. 'available' → green "· available" on the
    // nights line; 'unavailable' → the amber warning + the opt-in gate; null →
    // no feedback (no dates, or no availability data).
    const availabilityState = computed(
        () => rangeAvailability(enquiryDates.from, enquiryDates.to, availability.value)
    );

    // ─── Booked-dates opt-in gate ────────────────────────────────────────────
    // A range the calendar shows as booked disables Send until this is ticked.
    // The tick becomes the payload's availability_checked value, so the server
    // gate (which requires it when the property publishes availability) is
    // enforced for real rather than always satisfied.
    const sendAnyway = ref(false);

    // Nearest available dates at THIS house that match the requested stay's shape
    // (same nights + check-in weekday). Tapping one sets the enquiry to those
    // dates — flipping the range to Available and keeping the lead on this house.
    const sameDateAlternatives = ref([]);

    function applyAlternativeDates(alt) {
        setEnquiryDates(alt.startDate, alt.endDate);
    }

    // Similar houses to fall back to when this house is booked: confirmed-free
    // first, then houses that don't publish availability ("worth checking"), so
    // the guest always has somewhere to go rather than a dead end. Plain listing
    // links — view-first, no shortcut sends.
    const alternatives = ref([]);
    let alternativesRequest = 0; // stale-response guard (monotonic request id)

    function fetchAlternatives() {
        const requestId = ++alternativesRequest;

        const guests =
            (parseInt(formData.value.adults_number, 10) || 0) +
            (parseInt(formData.value.children_number, 10) || 0);

        axios.get(`/wp-json/lh/v1/properties/${Page.postId}/similar`, {
            params: {
                start_date: enquiryDates.from,
                end_date: enquiryDates.to,
                guests,
                favourite_ids: getFavourites(),
            },
        })
            .then((response) => {
                if (requestId !== alternativesRequest) return;

                const confirmed = (response.data.confirmed || []).map((h) => ({ ...h, confirmed: true }));
                const unconfirmed = (response.data.unconfirmed || []).map((h) => ({ ...h, confirmed: false }));

                // Confirmed-free first; pad with non-publishing houses up to 3.
                alternatives.value = confirmed.concat(unconfirmed).slice(0, 3);
            })
            .catch(() => {
                if (requestId !== alternativesRequest) return;

                // Alternatives are a bonus — on failure show the notice without them.
                alternatives.value = [];
            });
    }

    // A new range invalidates any previous opt-in — the confirmation is specific
    // to the dates it was ticked for. Watches the dates (not just the state) so
    // moving between two different booked ranges also resets and refetches.
    //
    // immediate: dates arrive prefilled from the enquiry_* cookies (a range picked
    // on another house, or on a previous visit), so a guest can land on a booked
    // range without ever changing it. Without this the notice rendered with no
    // alternative dates and no similar houses — just the warning and the opt-in,
    // i.e. only the worst of the three options it exists to offer.
    watch(() => [enquiryDates.from, enquiryDates.to, availabilityState.value], ([from, to, state]) => {
        sendAnyway.value = false;

        if (state === 'unavailable') {
            sameDateAlternatives.value = sameShapeAvailableWindows(from, to, availability.value, 3);
            fetchAlternatives();
        } else {
            sameDateAlternatives.value = [];
            alternatives.value = [];
        }
    }, { immediate: true });

    // Property payment terms (plain text) + credit-cards icon (SVG HTML), rendered
    // as quiet fine print beneath submit.
    const paymentTerms = (typeof EnquiryForm !== 'undefined' && EnquiryForm.data)
        ? (EnquiryForm.data.payment_terms || '')
        : '';
    const cardPaymentsIcon = (typeof EnquiryForm !== 'undefined' && EnquiryForm.data)
        ? (EnquiryForm.data.card_payments_icon || '')
        : '';

    // Smoothly scroll to the availability calendar (in the #availability section
    // above the form) from the label-row link.
    function scrollToCalendar() {
        const el = document.getElementById('availability');
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    // Keep the payload fields in lockstep with the store — dates can be set from
    // the calendar after mount, and formData.from / .to are what actually ship.
    watch(() => [enquiryDates.from, enquiryDates.to], ([from, to]) => {
        formData.value.from = from;
        formData.value.to = to;
    });

    // ─── localStorage prefill ────────────────────────────────────────────────
    //
    // Persists enquiry fields across property visits CLIENT-SIDE only — never
    // crosses the wire, so it can't leak through Cloudflare's anonymous cache
    // the way the old enquiry_* cookies could. One key per field, prefixed
    // lh_enquiry_; values are JSON-encoded so booleans / numbers round-trip
    // cleanly. Dates stay on the existing enquiry_start_date / enquiry_end_date
    // cookies because PropertyPriceChecker owns them and the within-session
    // cross-property flow is already solved there.
    //
    // The availability checkbox is intentionally NOT in this list: it confirms
    // the user looked at THIS property's calendar, so persisting it would let
    // them tick on Property A and submit Property B without engaging with B.
    // It's always reset to false on mount below, even if the PHP data ever
    // ships a truthy value.
    const STORAGE_PREFIX = 'lh_enquiry_';
    const PERSISTED_FIELDS = [
        'first_name',
        'last_name',
        'email',
        'phone',
        'group_type',
        'adults_number',
        'children_number',
        'event_details',
        'newsletter_subscribe',
    ];

    // localStorage throws in some privacy modes / when quota is exceeded /
    // when the API is disabled by enterprise policy. Wrap every read and write
    // so the form just degrades silently to "no prefill" rather than crashing.
    function readStorage(field) {
        try {
            const raw = window.localStorage.getItem(STORAGE_PREFIX + field);
            return raw === null ? null : JSON.parse(raw);
        } catch (e) {
            return null;
        }
    }

    function writeStorage(field, value) {
        try {
            window.localStorage.setItem(STORAGE_PREFIX + field, JSON.stringify(value));
        } catch (e) {
            // private mode / quota exceeded / disabled — silently no-op.
        }
    }

    // Called from each persisted field's @blur (or @change for checkbox/select)
    // so writes happen once per edit, not per keystroke.
    // ─── Form progress ────────────────────────────────────────────────────
    // GA4 carries one form_start and one enquiry_submitted, so everything
    // between them is a black box – and form_start fires on the FIRST
    // interaction with anything in the form (opening the date picker counts),
    // so the gap between the two cannot be read as abandonment either.
    //
    // These fire once per field per page, plus one event naming the fields
    // that blocked a submit. FIELD NAMES ONLY – no values ever reach the
    // dataLayer, so nothing a guest typed leaves the page this way.
    //
    // A field counts as reached on blur OR the first time its value changes
    // (see watchFieldReach below). Blur alone undercounted badly: phone
    // autofill fills name, email and phone without focusing them, so in the
    // 28 days to 7 Oct 2026 "email reached" read 500 on phones while 849
    // phone enquiries were sent – and every one of those had an email.
    const fieldsReached = new Set();

    function trackFieldReached(field) {
        if (fieldsReached.has(field)) return;
        fieldsReached.add(field);

        pushToDataLayer({
            event: 'enquiry_field_reached',
            form_field: field,
            form_field_order: fieldsReached.size,
        });
    }

    // Which field turned a send attempt back. This is the number that says
    // whether the confirm field – or anything else – is actually costing
    // enquiries, rather than us inferring it from a total.
    function trackSubmitBlocked(errors) {
        pushToDataLayer({
            event: 'enquiry_submit_blocked',
            blocking_fields: Object.keys(errors).sort().join(','),
        });
    }

    // Focus lands in the textarea it just revealed - the click was a request to
    // write something, so the caret should already be where the writing goes.
    function openEventDetails() {
        eventDetailsOpen.value = true;
        nextTick(() => eventDetailsElem.value?.focus());
    }

    // The value half of "reached". Armed only once mount has finished seeding
    // the form, so a value the page or localStorage filled in is the
    // baseline, not something the guest did. email_confirm is left to blur:
    // the form fills it itself when an email is pasted or restored, and then
    // collapses it, so a value there says nothing about the guest getting to it.
    const VALUE_REACH_FIELDS = [
        'group_type',
        'adults_number',
        'children_number',
        'event_details',
        'first_name',
        'last_name',
        'email',
        'phone',
        'newsletter_subscribe',
    ];
    let reachBaseline = null;

    watch(() => VALUE_REACH_FIELDS.map((field) => formData.value[field]), (values) => {
        if (!reachBaseline) return;

        VALUE_REACH_FIELDS.forEach((field, i) => {
            const value = values[i];
            const blank = value === '' || value === null || value === undefined || value === false;

            if (!blank && value !== reachBaseline[field]) {
                trackFieldReached(field);
            }
        });
    });

    function armFieldReach() {
        reachBaseline = Object.fromEntries(VALUE_REACH_FIELDS.map((field) => [field, formData.value[field]]));
    }

    function persistField(field) {
        // Before the early return: a field the form doesn't persist (phone,
        // the honeypot's neighbours) is still a field the guest reached.
        trackFieldReached(field);

        if (!PERSISTED_FIELDS.includes(field)) return;
        writeStorage(field, formData.value[field]);
    }

    onMounted(() => {
        loaded.value = true;

        // Seed empty fields from localStorage — never clobber a value the PHP
        // data injection or any URL-param prefill already set. Booleans treat
        // `false` as "empty" so an offers tick on a prior property can win.
        for (const field of PERSISTED_FIELDS) {
            const current = formData.value[field];
            const isEmpty = current === '' || current === null || current === undefined || current === false;
            if (!isEmpty) continue;

            const stored = readStorage(field);
            if (stored === null || stored === '') continue;

            formData.value[field] = stored;
        }

        if (formData.value.event_details) {
            eventDetailsOpen.value = true;
        }

        // Email/confirm-email coupling: when email was restored from
        // localStorage, mirror it into the confirm field and mark the email
        // as "already verified" via emailPasted. The confirm field collapses
        // (#enquiry-form form[data-confirm-collapsed=true] .enquiry-confirm)
        // so the user isn't asked to retype an address they confirmed on a
        // prior property. Manual edit later still re-opens the confirm field.
        if (formData.value.email && !formData.value.email_confirm) {
            formData.value.email_confirm = formData.value.email;
            emailPasted.value = true;
        }

        // The form only renders once `loaded` is true, so wait for the DOM flush.
        // Field-reach tracking is armed in the same tick, after the seeding
        // above has been through the watcher, so pre-filled values never count.
        nextTick(() => {
            bindEmailPasteSync();
            armFieldReach();
        });
    });

    function markFormStarted() {
        if (formStarted.value) return;
        formStarted.value = true;

        window.dispatchEvent(new CustomEvent('landedHouses/enquiry/form_start'));

        pushToDataLayer({ event: 'form_start' });
    }

    // Set an input's value through the native setter so Vue's v-model reactivity fires.
    function setNativeInputValue(el, value) {
        const descriptor = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value');

        descriptor.set.call(el, value);
        el.dispatchEvent(new Event('input', { bubbles: true }));
    }

    function bindEmailPasteSync() {
        const emailEl = document.getElementById('email');
        const confirmEl = document.getElementById('email-confirm');

        if (!emailEl || !confirmEl) {
            return;
        }

        emailEl.addEventListener('paste', () => {
            // Let the pasted text land in the field first, then mirror it into confirm.
            setTimeout(() => {
                setNativeInputValue(confirmEl, emailEl.value);
                emailPasted.value = true;
            }, 0);
        });

        // Autofill does NOT fire `paste`. It fires `input`, so before this the
        // whole address arrived, fell through to the manual-edit branch below,
        // and OPENED the confirm field – for the one entry method most people
        // on a phone use, on a site where iOS is half the property traffic.
        // A machine entry is any value that arrives all at once: humans type
        // one character at a time, so a jump of more than one character means
        // autofill, a password manager or a keyboard suggestion. Treated
        // exactly like a paste – mirrored into confirm, which then collapses.
        let previousValue = emailEl.value;

        emailEl.addEventListener('input', (event) => {
            const currentValue = emailEl.value;
            const arrivedAtOnce = currentValue.length - previousValue.length > 1
                || event.inputType === 'insertReplacementText';

            previousValue = currentValue;

            // The paste flow keeps confirm in sync itself; nothing to undo or re-check here.
            if (event.inputType === 'insertFromPaste') {
                return;
            }

            if (arrivedAtOnce && currentValue) {
                setNativeInputValue(confirmEl, currentValue);
                emailPasted.value = true;

                return;
            }

            // A manual edit after a paste, or clearing Email entirely, resets the (now
            // hidden) confirm field so it re-appears empty when Email next has content.
            // Validation runs on blur/submit; the formData watcher live-clears any error.
            if (emailPasted.value || !emailEl.value) {
                setNativeInputValue(confirmEl, '');
                emailPasted.value = false;
            }
        });
    }

    const EMAILS_DONT_MATCH = "Emails don't match – please check";
    const ZERO_NIGHT_MESSAGE = 'Please choose a check-out date after your check-in date.';
    const GENERIC_SEND_ERROR = 'Sorry, your enquiry couldn’t be sent. Please try again, or call us if it keeps failing.';

    /** First message out of the server's { field: { rule: message } } error bag. */
    function firstErrorMessage(errors) {
        const field = Object.values(errors)[0];

        return field ? Object.values(field)[0] : '';
    }

    function isValidEmail(value) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
    }

    // Conversational, client-side validators for every required field — each returns an
    // error string or null. These (not the browser) drive every visible message, shown on
    // blur and on submit through InlineValidation. The <form> carries `novalidate`, so the
    // native required / type=email attributes stay for accessibility without popping the
    // browser's own tooltips. Phone is intentionally absent — it's optional.
    const fieldValidators = {
        first_name: () => formData.value.first_name.trim() ? null : 'Please enter your first name',
        last_name: () => formData.value.last_name.trim() ? null : 'Please enter your last name',
        email: () => {
            if (!formData.value.email.trim()) return 'Please enter your email address';
            if (!isValidEmail(formData.value.email)) return 'Please enter a valid email address';
            return null;
        },
        email_confirm: () => {
            if (!showConfirm.value) return null; // hidden until Email has content / after a paste
            if (!formData.value.email_confirm.trim()) return 'Please confirm your email address';
            if (formData.value.email_confirm !== formData.value.email) return EMAILS_DONT_MATCH;
            return null;
        },
        group_type: () => formData.value.group_type ? null : 'Please select a group type',
        // Dates are optional; the only blocking rule is a zero-night range
        // (from == to). A reversed range never reaches here — the store swaps it.
        dates: () => {
            const { from, to } = formData.value;
            if (!from || !to) return null;
            if (from === to) return ZERO_NIGHT_MESSAGE;
            return null;
        },
    };

    function clearFieldError(field) {
        if (!Object.prototype.hasOwnProperty.call(validationErrors.value, field)) {
            return;
        }

        const next = { ...validationErrors.value };
        delete next[field];
        validationErrors.value = next;
    }

    function setFieldError(field, message) {
        if (!message) {
            clearFieldError(field);
            return;
        }

        validationErrors.value = { ...validationErrors.value, [field]: [message] };
    }

    // Validate one field — used on blur.
    function validateField(field) {
        trackFieldReached(field);

        if (fieldValidators[field]) {
            setFieldError(field, fieldValidators[field]());
        }
    }

    // Validate everything — used on submit. Returns true when the form is valid.
    function validateAll() {
        const errors = {};

        for (const field in fieldValidators) {
            const message = fieldValidators[field]();
            if (message) {
                errors[field] = [message];
            }
        }

        validationErrors.value = errors;

        const valid = Object.keys(errors).length === 0;

        if (!valid) {
            trackSubmitBlocked(errors);
        }

        return valid;
    }

    // Live-clear: once a field is showing an error, re-validate it as the user edits so the
    // message clears the moment it's fixed — without flagging fields they haven't touched.
    watch(formData, () => {
        for (const field in fieldValidators) {
            if (Object.prototype.hasOwnProperty.call(validationErrors.value, field)) {
                setFieldError(field, fieldValidators[field]());
            }
        }
    }, { deep: true });

    // Fetch similar houses free on the submitted dates and keep up to 3 —
    // confirmed-available first, padded with unconfirmed only to reach 3.
    function fetchSimilar() {
        // Mirror the server guard: no dates → no suggestions. The success message
        // still shows; this block simply stays empty.
        if (!formData.value.from || !formData.value.to) {
            pushSimilarShownEvent(0, 'no_dates');

            return;
        }

        const guests =
            (parseInt(formData.value.adults_number, 10) || 0) +
            (parseInt(formData.value.children_number, 10) || 0);

        // Show the loader immediately — the /similar query isn't instant, and the
        // section sits inside the confirmation card so the wait reads as "finding
        // houses" rather than a blank gap that later pops in.
        suggestionsLoading.value = true;

        axios.get(`/wp-json/lh/v1/properties/${Page.postId}/similar`, {
            params: {
                start_date: formData.value.from,
                end_date: formData.value.to,
                guests,
                // The visitor's locally-saved houses — the server surfaces these
                // first (subject to the same availability check) and tags each
                // card with `favourite` so it can carry a "Saved" chip.
                favourite_ids: getFavourites(),
            },
        })
            .then((response) => {
                const confirmed = (response.data.confirmed || []).map((house) => ({
                    ...house,
                    badge: 'Available on your dates',
                    confirmed: true,
                }));

                const unconfirmed = (response.data.unconfirmed || []).map((house) => ({
                    ...house,
                    badge: 'Check availability',
                    confirmed: false,
                }));

                // Confirmed first; only pad with unconfirmed to reach 3.
                suggestions.value = confirmed.concat(unconfirmed).slice(0, 3);

                pushSimilarShownEvent(
                    suggestions.value.length,
                    suggestions.value.length ? 'ok' : 'none_returned',
                );
            })
            .catch(() => {
                // Suggestions are a bonus — on any failure render nothing at all.
                suggestions.value = [];

                pushSimilarShownEvent(0, 'request_failed');
            })
            .finally(() => {
                suggestionsLoading.value = false;
            });
    }

    // Fire the GA4 conversion event — ONLY from a confirmed-successful send, never
    // on click or validation failure — attributed to the property actually
    // contacted (so a similar-house enquiry doesn't misattribute to the page's own
    // property). Pushed to the dataLayer for GTM.
    //
    // Immediate: this runs in the request's callback, not in a tap, so
    // deferring it saves no INP – and the direct_email path can set
    // location.href in the same tick, where a queued conversion would rest on
    // the pagehide flush.
    function pushEnquiryEvent(enquiryType, slug, id) {
        pushToDataLayer({
            event: 'enquiry_submitted',
            enquiry_type: enquiryType,
            enquired_property: slug,
            enquired_property_id: id,
        }, { immediate: true });
    }

    // Module-usage signal for the view-first flow (#191): fires when a
    // non-saved suggestion's "View house" link is tapped. Enquiry events stay
    // success-only and untouched; without this the similar-houses block is a
    // black box between "shown" and any eventual enquiry on the destination
    // listing. GA4 sends it via beacon, so it survives the navigation.
    // Immediate, not deferred: this tap navigates away, so the next frame
    // belongs to the next page.
    function pushSimilarViewEvent(house) {
        pushToDataLayer({
            event: 'similar_house_view',
            viewed_property: house.slug,
            viewed_property_id: house.id,
        }, { immediate: true });
    }

    // Impression signal for the block as a whole. similar_house_view only fires
    // on a "View house" tap (and saved cards show a send button instead, firing
    // similar_house_email), so with those alone "never rendered" and "rendered
    // and ignored" are the same number – zero. Fires once per successful
    // enquiry, INCLUDING when the count is zero: the zero cases are the whole
    // point, and similar_reason says which gate closed.
    //
    //   ok             – cards rendered
    //   no_dates       – enquiry carried no dates, so the fetch never ran
    //   none_returned  – endpoint answered with both tiers empty
    //   request_failed – the fetch errored
    function pushSimilarShownEvent(count, reason) {
        pushToDataLayer({
            event: 'similar_houses_shown',
            similar_count: count,
            similar_reason: reason,
        }, { immediate: true });
    }

    // Re-submit the IDENTICAL enquiry to a single suggested house via the existing
    // enquire endpoint (one card, one tap).
    function sendSameEnquiry(house) {
        if (sentIds.value.includes(house.id) || sendingIds.value.includes(house.id)) {
            return Promise.resolve();
        }

        // Clear any prior error for this card so a retry (tapping the error state)
        // starts clean and shows the spinner again.
        if (sendErrors.value[house.id]) {
            const next = { ...sendErrors.value };
            delete next[house.id];
            sendErrors.value = next;
        }

        sendingIds.value = [...sendingIds.value, house.id];

        // Reuse the guest's exact enquiry. availability_checked is forced true:
        // these houses are genuinely free on the dates and the guest is explicitly
        // opting in with this tap, so the server's availability gate is satisfied.
        // multi_enquiry flags these apart from a primary, single-house enquiry.
        const payload = {
            ...formData.value,
            availability_checked: true,
            multi_enquiry: 1,
        };

        return axios.post(`/wp-json/lh/v1/properties/${house.id}/enquire`, payload, {
            headers: {
                'Content-Type': 'application/json',
                'X-WP-Nonce': Page.nonce,
            },
        })
            .then(() => {
                sentIds.value = [...sentIds.value, house.id];

                // Attribute to the SIMILAR property that was contacted (the card's
                // own house.id/slug from /similar), NOT the page's property.
                pushEnquiryEvent('similar_house_email', house.slug, house.id);
            })
            .catch((error) => {
                // Visible, retryable failure — never silent. Prefer the server's
                // specific validation message (first error) over the generic copy;
                // the button stays enabled so tapping it retries.
                console.error('Similar-house enquiry failed', error?.response ?? error);

                const responseErrors = error?.response?.data?.data?.errors || {};
                const firstField = Object.values(responseErrors)[0];
                const firstError = firstField ? Object.values(firstField)[0] : null;

                const message = firstError
                    || error?.response?.data?.message
                    || 'Couldn’t send – tap to try again.';

                sendErrors.value = { ...sendErrors.value, [house.id]: message };

                // No dataLayer push here — enquiry_submitted stays success-only.
            })
            .finally(() => {
                sendingIds.value = sendingIds.value.filter((id) => id !== house.id);
            });
    }

    function submitForm() {
        // Synchronous re-entry guard — the real double-submit lock. The button's
        // :disabled="submitting" binding is a UI affordance only: Vue flushes that
        // attribute to the DOM on the next tick, so a fast second tap (or a
        // touch+click "ghost", or Enter+tap) can beat the flush and re-enter while
        // a slow request is still in flight, firing a second axios.post. Checking
        // the flag here, before any network call, closes that window regardless of
        // when the DOM actually reflects disabled. Reset in finally{} below so a
        // genuinely failed submit can still be retried.
        if (submitting.value) return;
        submitting.value = true;

        successMessage.value = '';
        errorMessage.value = '';

        // Sync dates from the shared store right before posting. The store is the
        // single source of truth (inline inputs + calendar both write it, and it
        // mirrors the enquiry_start_date / enquiry_end_date cookies), so this
        // guarantees the latest selection ships even if it changed after mount.
        formData.value.from = enquiryDates.from;
        formData.value.to = enquiryDates.to;

        // Booked dates ship the guest's explicit opt-in; available/unknown ranges
        // ship true as before. The disabled Send button is the UI gate — this is
        // what the server actually checks.
        formData.value.availability_checked = availabilityState.value === 'unavailable'
            ? sendAnyway.value
            : true;

        // Conversational client-side validation gates submit; the server re-validates as a backstop.
        if (!validateAll()) {
            // The flag is now set up-front (inseparable from the guard), so this
            // early return must release it — this path never reaches the axios
            // .finally() below — otherwise a validation failure would leave the
            // button disabled with the spinner stuck on.
            submitting.value = false;
            scrollToError();
            return;
        }

        axios.post(`/wp-json/lh/v1/properties/${Page.postId}/enquire`, formData.value, {
            headers: {
                'Content-Type': 'application/json',
                'X-WP-Nonce': Page.nonce,
            },
        })
            .then(response => {
                successMessage.value = response.data.message;
                successFollowUp.value = response.data.follow_up || '';

                // Conversion event on CONFIRMED send only, attributed to this page's
                // property. Pushed before any redirect so it can't be skipped.
                pushEnquiryEvent('direct_email', Page.slug, Page.postId);

                if (response.data.redirect_url) {
                    window.location.href = response.data.redirect_url;
                } else {
                    scrollToSuccess();
                    fetchSimilar();
                }
            })
            .catch(error => {
                console.log(error);

                const responseErrors = error?.response?.data?.data?.errors || {};

                if (Object.keys(responseErrors).length) {
                    validationErrors.value = responseErrors;

                    // The form only renders an InlineValidation slot for the fields
                    // it actually asks for. A server error keyed to anything else
                    // (availability_checked, id, the honeypot) would be stored and
                    // never shown — Send would look like it simply did nothing.
                    // Checked against the rendered DOM rather than a list of keys so
                    // it can't drift as fields come and go.
                    nextTick(() => {
                        if (! form.value?.querySelector('.inline-validation')) {
                            errorMessage.value = firstErrorMessage(responseErrors)
                                || error?.response?.data?.message
                                || GENERIC_SEND_ERROR;
                        }

                        scrollToError();
                    });

                    return;
                }

                // A failure with no body at all (offline, a 500, a timeout) still has
                // to say something — an empty message renders no FormError, which is
                // the same silent dead end.
                errorMessage.value = error?.response?.data?.message || GENERIC_SEND_ERROR;

                scrollToError();
            })
            .finally(() => {
                submitting.value = false;
            });
    }

    // Align the confirmation to the TOP of the viewport, not its centre.
    //
    // This runs 100ms after success, while fetchSimilar() is still in flight and
    // the card holds only the confirmation and a loader (~459px on a phone). The
    // suggestions then land and grow it to ~1416px. Centring pins the card's
    // MIDDLE, so all of that growth is pushed downward off-screen: measured at
    // 380x780 and 390x844, not one "View house" button ended up on screen, which
    // is the only thing similar_house_view can fire from.
    //
    // Aligning the top makes the later growth harmless – the top never moves, so
    // the first card and its CTA land in the space below. Preferred over
    // re-scrolling once the cards arrive, which would yank the page under anyone
    // who had already started reading. scroll-mt on the element clears the fixed
    // header (see the class on the wrapper above).
    function scrollToSuccess() {
        setTimeout(() => {
            let elem = successMessageElem.value;

            if (elem) {
                elem.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                    inline: 'center',
                });
            }
        }, 100);
    }

    function scrollToError() {
        setTimeout(() => {
            let errorElem = null;

            if (errorMessage.value && errorMessageElem.value) {
                errorElem = errorMessageElem.value;
            } else {
                errorElem = form.value.querySelector('.inline-validation');
            }

            if (errorElem) {
                errorElem.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'center',
                });
            }
        }, 100);
    }
</script>
